/**
 * AssetModal.jsx — shared create/edit/view modal for art_assets (Phase 6, 2026-02).
 *
 * Used from three entry points:
 *   1. Global "+ Add Asset" button in the Art dept header
 *   2. ArtByScene drawer prop columns (category + scene pre-filled)
 *   3. ArtByLocation block header (location pre-filled)
 *   4. (Deferred to Phase 8) Character page
 *
 * Modes:
 *   mode="create"  — POST /art/assets
 *   mode="edit"    — PATCH /art/assets/{id}
 *   mode="view"    — read-only (author later; today edit is always
 *                     available to write-gated users so we fold view→edit).
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { X, Trash2, Upload, Link2 } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import {
  ASSET_STATUS_ORDER, ASSET_STATUS_TOKENS,
  ASSET_CATEGORIES, ASSET_CATEGORY_TOKENS,
  StatusPill,
} from './artShared';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', category: 'scene_prop', description: '',
  tags: [], source: '', cost: '', status: 'needed',
  linked_scene_ids: [], linked_location_ids: [],
  character_id: null, character_sub_category: null,
};

export default function AssetModal({
  open, mode = 'create', asset = null, prefill = null,
  projectId, token, scenes = [], locations = [],
  onClose, onSaved, onDeleted,
}) {
  const [form, setForm] = useState(EMPTY);
  const [photoRows, setPhotoRows] = useState([]);  // [{file_id, storage_path}]
  const [saving, setSaving] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const fileRef = useRef(null);

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setForm({
        name: asset.name || '', category: asset.category || 'scene_prop',
        description: asset.description || '',
        tags: asset.tags || [],
        source: asset.source || '', cost: asset.cost || '',
        status: asset.status || 'needed',
        linked_scene_ids: asset.linked_scene_ids || [],
        linked_location_ids: asset.linked_location_ids || [],
        character_id: asset.character_id || null,
        character_sub_category: asset.character_sub_category || null,
      });
      setPhotoRows(asset.photos || []);
    } else {
      setForm({ ...EMPTY, ...(prefill || {}) });
      setPhotoRows([]);
    }
  }, [open, asset, prefill]);

  if (!open) return null;

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.character_id && !form.character_sub_category) {
      toast.error('Choose a character sub-category');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (mode === 'edit' && asset?.id) {
        const r = await authAxios.patch(`/projects/${projectId}/art/assets/${asset.id}`, form);
        saved = r.data;
      } else {
        const r = await authAxios.post(`/projects/${projectId}/art/assets`, form);
        saved = r.data;
      }
      onSaved?.(saved);
      toast.success(mode === 'edit' ? 'Saved' : 'Asset created');
      onClose?.();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!asset?.id) return;
    if (!window.confirm(`Delete "${asset.name}"? This cannot be undone.`)) return;
    try {
      await authAxios.delete(`/projects/${projectId}/art/assets/${asset.id}`);
      onDeleted?.(asset);
      toast.success('Deleted');
      onClose?.();
    } catch (e) { console.error(e); toast.error('Delete failed'); }
  };

  const uploadPhoto = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (mode !== 'edit' || !asset?.id) {
      toast.info('Save the asset first, then add photos.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(
        `/projects/${projectId}/art/assets/${asset.id}/photos`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPhotoRows(prev => [...prev, { file_id: r.data.file_id, storage_path: r.data.storage_path }]);
      toast.success('Photo added');
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };
  const removePhoto = async (fid) => {
    if (!asset?.id) return;
    try {
      await authAxios.delete(`/projects/${projectId}/art/assets/${asset.id}/photos/${fid}`);
      setPhotoRows(prev => prev.filter(x => x.file_id !== fid));
    } catch (e) { console.error(e); toast.error('Remove failed'); }
  };

  const toggleScene = (sid) => setForm(f => ({
    ...f,
    linked_scene_ids: f.linked_scene_ids.includes(sid)
      ? f.linked_scene_ids.filter(x => x !== sid)
      : [...f.linked_scene_ids, sid],
  }));
  const toggleLoc = (lid) => setForm(f => ({
    ...f,
    linked_location_ids: f.linked_location_ids.includes(lid)
      ? f.linked_location_ids.filter(x => x !== lid)
      : [...f.linked_location_ids, lid],
  }));

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (!form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }));
    setTagDraft('');
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="asset-modal">
      <div className="w-[720px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
        <header className="sticky top-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
          <span className="text-sm font-bold text-[var(--color-accent)]">
            {mode === 'edit' ? 'Edit asset' : 'Add asset'}
          </span>
          <StatusPill status={form.status} className="ml-1" />
          <button onClick={onClose} className="ml-auto text-[var(--color-text-dim)] hover:text-white" data-testid="asset-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Category</div>
            <div className="flex gap-1 flex-wrap">
              {ASSET_CATEGORIES.map(k => (
                <button key={k} type="button"
                  onClick={() => setForm(f => ({ ...f, category: k }))}
                  className={`text-[11px] px-2 py-1 rounded-full border ${form.category === k ? 'border-[var(--color-accent)] bg-[var(--color-panel)]' : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]'}`}
                  style={{ color: form.category === k ? ASSET_CATEGORY_TOKENS[k].fg : '#a0a0a0' }}
                  data-testid={`asset-modal-cat-${k}`}>
                  {ASSET_CATEGORY_TOKENS[k].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Name *</div>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)]"
              placeholder="e.g. Antique brass kettle"
              data-testid="asset-modal-name" />
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1 flex items-center gap-2">
              <span>Photos</span>
              {mode === 'edit' && (
                <button type="button" onClick={uploadPhoto}
                  className="ml-auto text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                  data-testid="asset-modal-upload-photo">
                  <Upload className="w-3 h-3 inline mr-1" /> Add photo
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            </div>
            {photoRows.length === 0 ? (
              <div className="text-[11px] italic text-[var(--color-text-mute)]">
                {mode === 'edit' ? 'No photos yet.' : 'Save the asset first to attach photos.'}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {photoRows.map(r => (
                  <div key={r.file_id} className="relative group aspect-square rounded overflow-hidden border border-[var(--color-border)]">
                    <AuthImage path={r.storage_path} className="w-full h-full object-cover" alt="" />
                    <button type="button" onClick={() => removePhoto(r.file_id)}
                      className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5"
                      title="Remove photo"
                      data-testid={`asset-modal-photo-delete-${r.file_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Description</div>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)] min-h-[70px]"
              data-testid="asset-modal-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Source</div>
              <input value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
                placeholder="vendor / builder / borrowed_from"
                data-testid="asset-modal-source" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Cost</div>
              <input value={form.cost} onChange={(e) => setForm(f => ({ ...f, cost: e.target.value }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
                placeholder="₹ / $ or 'TBD'"
                data-testid="asset-modal-cost" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Status</div>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
              data-testid="asset-modal-status">
              {ASSET_STATUS_ORDER.map(s => (
                <option key={s} value={s}>{ASSET_STATUS_TOKENS[s].label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Tags</div>
            <div className="flex gap-1 flex-wrap items-center">
              {form.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-sec)]">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-[var(--color-priority-critical)]">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="add tag + Enter"
                className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-1.5 py-0.5 text-[11px] text-[var(--color-text)] w-32"
                data-testid="asset-modal-tag-input" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1 flex items-center gap-1">
              <Link2 className="w-3 h-3" /> Linked scenes ({form.linked_scene_ids.length})
            </div>
            <div className="flex gap-1 flex-wrap max-h-24 overflow-y-auto">
              {scenes.map(s => (
                <button key={s.id} type="button" onClick={() => toggleScene(s.id)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${form.linked_scene_ids.includes(s.id) ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-border-strong)]'}`}
                  data-testid={`asset-modal-scene-${s.scene_number}`}>
                  #{s.scene_number}
                </button>
              ))}
              {scenes.length === 0 && <span className="text-[11px] italic text-[var(--color-text-mute)]">No scenes loaded.</span>}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1 flex items-center gap-1">
              <Link2 className="w-3 h-3" /> Linked locations ({form.linked_location_ids.length})
            </div>
            <div className="flex gap-1 flex-wrap max-h-24 overflow-y-auto">
              {locations.map(l => (
                <button key={l.name} type="button"
                  onClick={() => l.id && toggleLoc(l.id)}
                  disabled={!l.id}
                  title={!l.id ? 'This location needs an address / write to promote before linking.' : ''}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${form.linked_location_ids.includes(l.id) ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-border-strong)]'} ${!l.id ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid={`asset-modal-loc-${l.name.toLowerCase().replace(/\s+/g,'-')}`}>
                  {l.name}
                </button>
              ))}
              {locations.length === 0 && <span className="text-[11px] italic text-[var(--color-text-mute)]">No locations loaded.</span>}
            </div>
          </div>

          {form.character_id && (
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">
                Character sub-category <span className="text-[var(--color-accent)]">*</span>
              </div>
              <select
                value={form.character_sub_category || ''}
                onChange={(e) => setForm(f => ({ ...f, character_sub_category: e.target.value || null }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)]"
                data-testid="asset-modal-char-sub">
                <option value="">— select —</option>
                <option value="hero_prop">Hero prop</option>
                <option value="wardrobe_accessory">Wardrobe accessory</option>
                <option value="personal_effect">Personal effect</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
          {mode === 'edit' && (
            <button type="button" onClick={doDelete}
              className="text-xs px-3 py-1 rounded bg-transparent border border-[var(--color-priority-critical)] text-[var(--color-priority-critical)] hover:bg-[var(--color-priority-critical)]/10"
              data-testid="asset-modal-delete">
              <Trash2 className="w-3 h-3 inline mr-1" /> Delete
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose}
              className="text-xs px-3 py-1 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]"
              data-testid="asset-modal-cancel">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="text-xs px-3 py-1 rounded bg-[var(--color-accent)] text-black font-bold disabled:opacity-50"
              data-testid="asset-modal-save">
              {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create asset')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
